import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Clock, AlertTriangle, Ban, PauseCircle } from 'lucide-react';
import { accountsAPI } from '@/lib/api';
import { formatInt, prettyPhase, prettyPlan, nextRequestForAccount } from '@/lib/format';
import { useAuth } from '@/contexts/AuthContext';

export default function Accounts() {
  const { user } = useAuth();
  const [accs, setAccs] = useState([]);
  useEffect(() => { accountsAPI.mine().then(setAccs); }, []);
  const userTerminated = !!user?.is_terminated;
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-sm mb-2">Simulated accounts</div>
          <h1 className="font-display text-3xl">Your programs</h1>
        </div>
        {userTerminated ? (
          <span className="chip chip-red font-bold" data-testid="accounts-terminated-pill">
            <Ban size={12} className="inline mr-1" /> ACCOUNT TERMINATED
          </span>
        ) : (
          <Link to="/plans" className="btn-primary btn-sm"><Plus size={14} /> Start Evaluation</Link>
        )}
      </div>
      {accs.length === 0 && (
        <div className="card-tech rounded-lg p-12 text-center">
          <div className="label-sm mb-2">No accounts yet</div>
          <h3 className="font-display text-2xl">You haven't started an evaluation.</h3>
          {!userTerminated && <Link to="/plans" className="btn-primary mt-6 inline-flex">Browse programs</Link>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accs.map(a => {
          const next = nextRequestForAccount(a);
          const isBreached = a.status === 'breached';
          const isTerminated = !!a.is_terminated;
          const isPaused = !!a.is_paused && !isTerminated;
          const hasPending = !!a.pending_request;
          const isDim = isBreached || isTerminated || isPaused;
          return (
            <Link key={a.account_id} to={`/accounts/${a.account_id}`}
                  className={`card-tech rounded-lg p-5 hover:border-white/20 relative ${isDim ? 'opacity-60' : ''} ${isTerminated ? 'border-[#ff3366]/50' : isPaused ? 'border-amber-500/40' : isBreached ? 'border-[#ff3366]/30' : ''}`}
                  data-testid={`acc-${a.account_id}`}>
              {isTerminated ? (
                <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono text-[#ff3366] uppercase tracking-widest">
                  <Ban size={11} /> Terminated
                </div>
              ) : isPaused ? (
                <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono text-amber-400 uppercase tracking-widest" data-testid={`paused-tag-${a.account_id}`}>
                  <PauseCircle size={11} /> Paused
                </div>
              ) : isBreached ? (
                <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-mono text-[#ff3366] uppercase tracking-widest">
                  <AlertTriangle size={11} /> Breached
                </div>
              ) : null}
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-xs">#{a.mt_login || 'PENDING'} · {prettyPlan(a.plan_id)}</div>
                  <div className="font-display text-2xl mt-1">${formatInt(a.account_size)}</div>
                </div>
                <span className={`chip ${isTerminated ? 'chip-red' : isPaused ? 'chip-yellow' : isBreached ? 'chip-red' : a.phase === 'funded' ? 'chip-green' : 'chip-yellow'}`}>
                  {isTerminated ? 'TERMINATED' : isPaused ? 'PAUSED' : prettyPhase(a.phase)}
                </span>
              </div>
              <div className="mt-4">
                <div className="label-xs">PROGRAM</div>
                <div className="text-sm mt-1">{a.plan_name}</div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                {isTerminated ? (
                  <span className="text-xs text-[#ff3366]">Permanently disabled by compliance</span>
                ) : isPaused ? (
                  <span className="text-xs text-amber-400/90">Paused — migration in progress</span>
                ) : hasPending && !isBreached ? (
                  <span className="chip chip-yellow" data-testid="acc-pending-badge">
                    <Clock size={10} className="inline mr-1" /> Pending admin review
                  </span>
                ) : isBreached ? (
                  <span className="text-xs text-zinc-500">Purchase a new program to restart</span>
                ) : next ? (
                  <span className="text-xs text-[#00ff66]">→ {next.label}</span>
                ) : (
                  <span className="text-xs text-zinc-500">Open account</span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
